// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 경로표와 관문
//
//  이 파일이 맡는 것 셋.
//    ① **관문** — 로그인했나 / 연결이 되나. 여기서 한 번만 판정한다.
//    ② **경로표** — apps/api/src/index.ts 의 ROUTES 와 같은 모양이다. 표 하나면 끝난다.
//    ③ **어느 화면을 언제 내려받나** — 아래 「나중에 내려받는 화면들」.
//
//  ★ 관문에서 로그인 화면으로 **주소를 바꿔 보내지 않는다.** 주소는 그대로 두고
//    그리는 화면만 바꾼다. 그래서 `/dangjin` 을 북마크한 사람이 로그인하면 그 자리에서
//    바로 `/dangjin` 이 이어진다 — 「로그인 후 어디로 보낼지」를 따로 기억할 필요가 없다.
//    (그 기억은 거의 항상 버그가 된다. 새 탭·뒤로가기·만료가 겹치면 엉뚱한 곳으로 간다.)
// ─────────────────────────────────────────────────────────────────────────

import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Button, Card, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useSession, type SessionState } from './auth/SessionProvider';
import { humanMessage, type ApiError, type Project } from './api/client';
import { navigate, usePath } from './router';
import { CenteredShell } from './components/Shell';
import { Contact } from './components/Contact';
import { Login } from './screens/Login';
import { Projects } from './screens/Projects';
import { NotFound } from './screens/NotFound';
import { NewProject } from './screens/admin/NewProject';
import { Invite, InviteBounce } from './screens/Invite';

// ═══ 나중에 내려받는 화면들 ═══════════════════════════════════════════════
//
//  ★★ **왜 이렇게 하나 — 로그인 화면 하나 보자고 3D 엔진을 통째로 받고 있었다.**
//
//    이 파일이 `ViewerScreen` 을 그냥 `import` 하면, 그 한 줄 때문에 Cesium(3D 엔진)이
//    첫 화면 꾸러미 안으로 딸려 들어온다. 즉 **로그인도 안 한 사람**, 초대 링크만 눌러 본
//    사람, `/s/{토큰}` 으로 도면 한 장만 볼 사람까지 전부 그걸 다 받고서야 화면을 본다.
//    현장에서 폰 LTE 로 여는 분에게 그대로 나타난다.
//
//    `lazy()` 는 「이 화면을 실제로 그리게 될 때 그때 받아라」는 뜻이다. 받는 동안은
//    아래 `<Suspense>` 의 회전자가 대신 있는다.
//
//  ⚠️⚠️ **`ShareVisitor` 도 여기 있어야 한다 — 빠뜨리기 제일 쉬운 형제다.**
//     `screens/ShareVisitor.tsx` 가 자기도 `ViewerScreen` 을 물고 있다. 그래서 이것 하나만
//     정적 `import` 로 남겨 두면 **Cesium 이 첫 화면으로 도로 끌려 들어오고 위의 노력이
//     통째로 없던 일이 된다.** 짐작이 아니라 그렇게 만들어 놓고 재 봤다:
//
//         ShareVisitor 만 정적  → 첫 화면 5,724 kB (`dist/index.html` 이 cesium 4,518 kB 를 preload)
//         지금처럼 둘 다 lazy   → 첫 화면   463 kB
//
//  ⚠️ 반대로 `Login` · `Projects` · `NotFound` · `NewProject` · `Invite` 는 **일부러
//     그대로 둔다.** 전부 첫 화면에서 곧바로 필요하거나(로그인·목록), 크기가 작아서
//     쪼개면 왕복만 한 번 더 생긴다.
//
//  ⚠️ `lazy()` 는 **`default` 내보내기**를 요구한다. `Members` 만 `default` 가 없어
//     `.then()` 으로 감싼다. (여기 「이름을 오타 내면 화면이 빈 채로 뜬다」고 적을 뻔했는데
//      **거짓이다** — 일부러 오타를 내 보니 `tsc --noEmit` 이 그 자리에서 잡는다:
//      `error TS2551: Property 'Membrs' does not exist … Did you mean 'Members'?`)
//  ★★ **이 규율을 지키는 시험: `src/bundleContract.spec.ts`** [2026-08-13]
//     여태 **그물이 하나도 없었다.** 아래 두 줄을 정적 `import` 로 바꿔도 `tsc` 도 vitest 도
//     전부 초록이고 화면도 멀쩡히 뜬다 — 느려질 뿐이라 아무도 모르고 몇 달 뒤
//     「원래 느렸다」가 된다. 그 시험은 위 실측표를 **자동으로 다시 재는 것**이다:
//     `dist/index.html` 을 직접 읽어 cesium·Viewer 덩이가 첫 화면에 걸렸는지 본다.
const ViewerScreen = lazy(() => import('./screens/Viewer'));
const ShareVisitor = lazy(() => import('./screens/ShareVisitor'));
const Upload = lazy(() => import('./screens/Upload'));
const Modules = lazy(() => import('./screens/admin/Modules'));
const Share = lazy(() => import('./screens/admin/Share'));
const Trash = lazy(() => import('./screens/admin/Trash'));
const Alignments = lazy(() => import('./screens/admin/Alignments'));
const Members = lazy(() => import('./screens/admin/Members').then((m) => ({ default: m.Members })));

// ═══ 경로표 ═══════════════════════════════════════════════════════════════
//  ⚠️ 현장은 **주소 첫 칸**에 온다(`/dangjin`). SCREEN-DESIGN.html 의 목업이 `example.com/…`
//     로 되어 있고, 그래서 설계 문서에 예약어 목록(`admin` `new` `api` …)이 있는 것이다.
//     즉 아래 고정 경로들은 전부 **예약어**이며, 현장 slug 로 쓸 수 없다.
//     새 고정 경로를 추가하면 SCREEN-DESIGN.html 의 RESERVED 목록에도 같이 넣어라 —
//     안 넣으면 언젠가 같은 이름의 현장이 만들어지고, 그 현장은 **영영 열 수 없다.**

interface Route {
  path: string;
  screen: () => ReactNode;
}

const ROUTES: readonly Route[] = [
  { path: '/', screen: () => <Projects /> },
  { path: '/new', screen: () => <NewProject /> },
];

/** 현장 주소로 쓸 수 있는 모양. SCREEN-DESIGN.html 의 checkSlug() 와 같은 규칙이다. */
const SLUG = /^[a-z][a-z0-9-]{1,39}$/;

// ═══ 관문보다 먼저 오는 주소 ══════════════════════════════════════════════

/** `preGate()` 가 고른 갈래. 이름이 곧 화면이다. */
export type PreGate = 'invite' | 'invite-bounce' | 'share-visitor';

/**
 * **로그인 판정보다 먼저** 봐야 하는 주소인가. 아니면 `null`.
 *
 * ★★ 이 세 줄의 **순서와 「관문보다 앞」이라는 위치**가 이 파일의 핵심이다.
 *   그래서 화면 그리는 코드에서 떼어내 **순수 함수**로 뽑았다 — `App.spec.ts` 가
 *   이 규칙을 시험으로 붙들고 있다. 여기를 고치면 그 시험이 먼저 빨개진다.
 *
 * ★ **인자에 로그인 상태가 없다.** 일부러다. 못 받으니 못 본다 —
 *   「관문보다 먼저」가 주석의 약속이 아니라 **함수 모양으로 강제된다.**
 */
export function preGate(path: string): PreGate | null {
  // ★★ `/join`(초대 수락)은 **관문보다 먼저** 본다.
  //    초대 링크를 받은 사람은 아직 로그인 전일 수도, 다른 계정으로 로그인돼 있을 수도 있다.
  //    Invite.tsx 가 그 분기를 스스로 갖는다. 관문을 먼저 태우면 로그인 화면만 보이고
  //    **자기가 초대장을 들고 왔다는 사실이 화면에서 통째로 사라진다.**
  if (path === '/join') return 'invite';

  // ⚠️ `/invite/{토큰}` 이 **여기까지 왔다는 것 자체가 이미 고장**이다 [2026-08-11].
  //    정상이면 서버(`getInviteExchange`)가 302 로 채가서 이 코드는 안 돈다.
  //    여기 도달 = 프록시·라우팅이 이 경로를 SPA 로 떨어뜨렸다는 뜻이고, 그러면
  //    쿠키가 없어 `/join` 이 「초대 확인 정보가 없습니다」를 띄운다 — 실제로 났던 사고다.
  //    토큰은 주소에 그대로 있으니 **스스로 서버로 되돌려 보낸다.**
  if (path.startsWith('/invite/')) return 'invite-bounce';

  // ★★ `/s/{토큰}`(공유 링크)도 **관문보다 먼저** 본다. 같은 자리에 있는 이유는 다르다 —
  //    초대는 「로그인 전일 수도 있다」지만, 공유 링크는 **로그인이 아예 없는 사람**이다.
  //    관문을 태우면 그 사람은 계정을 만들 수 없어 영원히 로그인 화면에 갇힌다.
  //    ⚠️ `ProjectRoute` 와 부딪히지 않는다: SLUG 정규식이 최소 2자라 `/s` 는 안 걸리고,
  //       `s`·`share` 는 `db/002_catalog.sql:153` 의 예약어라 현장 slug 로 만들 수 없다.
  //       (새 고정 경로를 더할 때는 SCREEN-DESIGN.html 의 RESERVED 목록도 같이 봐라.)
  if (path === '/s' || path.startsWith('/s/')) return 'share-visitor';

  return null;
}

// ═══ 관문 ═════════════════════════════════════════════════════════════════

export function App() {
  const { state, reload } = useSession();
  const path = usePath();

  // 로그인한 사람이 `/login` 을 직접 치면 목록으로. (effect 인 이유: 렌더 중에 주소를
  // 바꾸면 React 가 같은 렌더 안에서 두 번 그리게 되어 경고가 난다.)
  //
  // ⚠️ 이 effect 는 **`<Suspense>` 바깥**에 있어야 한다. 안에 두면 마침 그 순간 다른 화면
  //    꾸러미를 내려받는 중일 때 effect 가 그만큼 늦게 돈다. 그래서 App 이 갈래를 직접
  //    `return` 하지 않고 `screen()` 이 만든 것을 감싸서 돌려준다.
  useEffect(() => {
    if (state.status === 'authed' && path === '/login') navigate('/', { replace: true });
  }, [state.status, path]);

  // ⚠️ `<Suspense>` 는 **여기 한 겹**이다. `lazy()` 화면마다 따로 감싸면 같은 회전자를
  //    일곱 벌 관리하게 되고, 한 곳을 빠뜨리면 그 화면만 **빈 화면**으로 뜬다.
  //    fallback 은 `Booting` 과 **같은 모양**이다(0.35초 뒤 회전자 — 아래 Booting 참고).
  return (
    <Suspense fallback={<Booting text="화면을 준비하는 중입니다" />}>
      {screen(path, state, reload)}
    </Suspense>
  );
}

/**
 * 주소 + 로그인 상태 → 그릴 것 하나. **순서가 전부다.**
 *
 * ⚠️ 컴포넌트가 아니라 그냥 함수다(`<Screen/>` 이 아니라 `screen(...)` 으로 부른다).
 *   훅이 하나도 없어서 그래도 되고, 그래야 위 `App` 의 훅 순서가 갈래마다 달라지지 않는다.
 */
function screen(path: string, state: SessionState, reload: () => Promise<void> | void): ReactNode {
  // ★★ 관문보다 **먼저**. 왜인지는 `preGate()` 안에 갈래마다 적어 두었다.
  switch (preGate(path)) {
    case 'invite':
      return <Invite />;
    case 'invite-bounce':
      return <InviteBounce path={path} />;
    case 'share-visitor':
      return <ShareVisitor />;
  }

  if (state.status === 'loading') return <Booting />;

  // ★ 연결 실패와 401 을 **다른 화면**으로 그린다. 이 구분이 이 파일에서 제일 중요한 줄이다.
  //   뭉치면, API 를 안 켰을 때 로그인 화면이 뜨고 → 코드를 요청해도 아무 일이 없고 →
  //   사용자도 개발자도 "로그인이 고장 났다" 고 오해한다. 실제로는 서버가 안 떠 있을 뿐이다.
  if (state.status === 'error') return <Disconnected error={state.error} onRetry={() => void reload()} />;

  if (state.status === 'anon') return <Login />;

  // ─ 여기부터는 로그인된 상태다 ─────────────────────────────────────────
  const exact = ROUTES.find((r) => r.path === path);
  if (exact) return exact.screen();

  // ★ `permsStale` — `/api/projects` 가 실패해 perms 없는 목록으로 들어왔다는 표시.
  //   뷰어 상태줄이 「관리자가 껐다」와 「권한을 못 받았다」를 갈라 말하는 근거다.
  return <ProjectRoute path={path} projects={state.projects} permsStale={state.permsStale ?? false} />;
}

/**
 * `/{slug}` 와 그 아래 — 현장 화면들.
 *
 * ★ **내 목록에 있는 slug 일 때만** 화면을 그린다. 아니면 404 다.
 *   무엇이든 아무 주소에나 그리면 그 자체가 "그 현장은 존재한다" 는 확인이 되고,
 *   그러면 비멤버에게 404 를 주기로 한 설계(DB-DESIGN §7-5)가 화면에서 새어 나간다.
 *   지금은 목록이 곧 내 권한이라 이 판정이 정확하다.
 *
 * ⚠️ **화면의 판정은 편의이지 보안이 아니다.** 목록은 캐시이고 그 사이에 권한이 빠질 수 있다.
 *   진짜 판정은 각 화면이 부르는 API 가 한다 — 권한이 없으면 서버가 404 를 준다.
 *
 * ⚠️ [2026-08-11] 아래 갈래는 **전부 실제 화면이거나 `NotFound`** 다. 「준비 중」
 *   자리표시자(`screens/ComingSoon.tsx`)는 쓰는 곳이 하나도 남지 않아 **지웠다.**
 *   다시 만들 일이 생기면 위 규칙(내 목록에 있을 때만)을 그대로 지켜야 한다.
 */
function ProjectRoute({
  path,
  projects,
  permsStale,
}: { path: string; projects: Project[]; permsStale: boolean }) {
  const seg = path.slice(1).split('/');
  const slug = seg[0] ?? '';
  if (!SLUG.test(slug)) return <NotFound />;

  const project = projects.find((p) => p.slug === slug);
  if (!project) return <NotFound />;

  const sub = seg[1];

  // `/{slug}/members` — 구성원·초대
  if (sub === 'members') return <Members slug={slug} />;

  // `/{slug}/upload` — 파일 올리기
  if (sub === 'upload') return <Upload slug={slug} project={project} />;

  // `/{slug}/modules` — 기능 모듈 켜기/끄기
  // ⚠️ 여기에 `perms[]` 게이트를 걸지 마라. 아래 ProjectRoute 는 `/api/projects` 로 채운
  //    목록을 쓰므로 perms 는 있지만, 이 화면의 진짜 판정은 **서버의 `can_manage`** 다.
  //    권한이 없으면 API 가 404 를 준다(비멤버·권한없음은 403 이 아니다).
  if (sub === 'modules') return <Modules slug={slug} />;

  // `/{slug}/trash` — 휴지통 (지운 모델 30일 보관)
  // `/{slug}/share` — 공유 링크 (현장 통째 · 만료 필수)
  // ⚠️ 위와 같은 이유로 `perms[]` 게이트를 걸지 않는다. 진짜 판정은 서버의 `can.*` 다.
  if (sub === 'trash') return <Trash slug={slug} />;
  if (sub === 'share') return <Share slug={slug} />;

  // `/{slug}/alignments` — 선형(LandXML) 올리기·목록
  // ⚠️ 위와 같은 이유로 `perms[]` 게이트를 걸지 않는다. 진짜 판정은 서버의 `can.manage` 다.
  //    ★ 이 화면은 **열람자도 들어올 수 있다**(목록 읽기는 `model.view` 다). 뷰어에서
  //      어떤 선형을 고를 수 있는지 보려면 관리자가 아니어도 봐야 하기 때문이다.
  if (sub === 'alignments') return <Alignments slug={slug} />;

  /*
   * ⚠️⚠️ **`/{slug}/drawings` 를 없앴다** [2026-08-17 · db/029 · 사장님 지적]
   *   *"3dtile이랑 ifc 올리는거처럼 올리게 해달라니깐 이해를 못한거 같네"*
   *     올리기 → `/{slug}/upload` (IFC·ZIP 과 **같은 화면**)
   *     고르기 → 뷰어의 레이어 패널 「도면(DXF)」 줄
   *     지우기 → `/{slug}/upload` 의 「올린 자료 고치기 · 지우기」
 *              (도면은 `app.model` 의 딸림 표라 모델을 지우면 같이 간다)
 *     되살리기 → `/{slug}/trash`
 *   ⚠️ [고침 2026-09-04] 여기 원래 「지우기 → `/{slug}/trash`」였다. 그 화면은
 *      **되살리는 곳**이고 지우는 단추가 없다. 그 문장이 사장님을 헤매게 했다.
   *   ⓘ 선형(`/alignments`)은 **그대로 둔다.** 사장님이 도면만 지적하셨고, 선형은
   *     지금 별도 화면이 문제를 일으키지 않는다 — 근거 없이 같이 옮기지 않는다.
   */

  // `/{slug}` — 뷰어.
  // ⚠️ **`<Shell>` 안에 넣지 마라.** 화면 전체(absolute inset:0)를 쓰는 것이 전제다.
  if (!sub) {
    // ⚠️ `siteName` 은 **화면 PNG·PDF 의 머리말**에 찍힌다. 안 넘기면 종이에 「ClickViewer
    //    화면 캡처」만 남아, 여러 현장을 다루는 사람이 **어느 현장 사진인지 못 가린다.**
    return (
      <ViewerScreen
        slug={slug}
        perms={project.perms}
        permsStale={permsStale}
        belt={project.belt}
        siteName={project.name}
      />
    );
  }

  return <NotFound />;
}

// ═══ 관문이 그리는 두 화면 ════════════════════════════════════════════════

/**
 * 기다리는 동안 — ① 앱 시작(`/api/me` 의 답) ② 화면 꾸러미를 내려받는 중(`<Suspense>`).
 *
 * ★★ 회전자를 **0.35초 뒤에** 보여준다(global.css 의 `cv-delayed`).
 *   대부분은 그 전에 끝나고, 그때 회전자가 번쩍이면 화면이 더 느려 보인다.
 *   ⚠️ **여기 대신 빈 화면(`null`)을 내지 마라.** 0.35초 규칙이 있는 이유가 그것이다 —
 *      빈 화면은 「느리다」가 아니라 **「고장 났다」**로 읽힌다.
 *
 * ⚠️ 두 쓰임의 **문구만** 다르고 모양은 같다. 모양을 갈라 두면 한쪽만 고쳐진다.
 */
function Booting({ text = '로그인 상태를 확인하는 중입니다' }: { text?: string }) {
  return (
    <Center h="60vh">
      <Stack align="center" gap="sm" className="cv-delayed">
        <Loader color="cv" />
        <Text size="sm" c="dimmed">
          {text}
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * 서버에 못 붙었다. **로그아웃이 아니다.**
 *
 * 현장에서 신호가 나쁜 것과 세션이 끊긴 것은 사용자가 해야 할 일이 정반대다.
 * 여기서는 "다시 시도" 만 있으면 되고, 코드를 다시 받게 하면 안 된다 —
 * 코드를 받으려 해도 같은 이유로 실패한다.
 */
function Disconnected({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  return (
    <CenteredShell>
      <Stack gap="lg" mt="xl">
        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Title order={2}>
              {error.kind === 'offline' ? '서버에 연결하지 못했습니다' : '서버가 응답하지 못하고 있습니다'}
            </Title>
            <Text size="sm" c="dimmed">
              {humanMessage(error)}
            </Text>
            <Text size="sm" c="dimmed">
              로그아웃된 것이 아닙니다. 연결이 돌아오면 그대로 이어집니다.
            </Text>
            <Button onClick={onRetry}>다시 시도</Button>
          </Stack>
        </Card>

        {/* 요청 ID 는 서버 로그와 맞춰 보는 유일한 열쇠다. 문의할 때 이 값을 같이 준다.
            ⚠️ 404 에는 이 헤더가 **일부러 없다**(errors.ts). 없다고 버그가 아니다. */}
        {error.requestId ? (
          <Text size="xs" c="dimmed" className="cv-mono">
            요청 ID: {error.requestId}
          </Text>
        ) : null}

        <Contact prefix="계속 안 되면 연락 주세요" />
      </Stack>
    </CenteredShell>
  );
}
